import React, { useEffect, useState, useMemo, useCallback } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import {
  Input,
  Select,
  Button,
  DangerButton,
  SecondaryButton
} from '../styles/TableStyles';
import { mapColumnLabels } from '../styles/ColumnUtils';
import { savedToast, errorToast, deletedToast } from '../utils/toastUtils';

const READONLY_STYLE = { backgroundColor: 'var(--grey-100)', cursor: 'not-allowed' };

const initialFormState = {
  BagType: '',
  People: '',
  BagsPerMinute: '',
  WagesPerHour: '',
};

const visibleColumns = mapColumnLabels([
  'BagType',
  'People',
  'BagsPerMinute',
  'WagesPerHour',
  'LaborPerBag',
]);

const fmt4 = (v) =>
  (v !== null && v !== '' && !isNaN(parseFloat(v)) ? `$${parseFloat(v).toFixed(4)}` : '');

const LaborCostsPage = () => {
  const [laborCosts, setLaborCosts] = useState([]);
  const [packTypes, setPackTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [submitting, setSubmitting] = useState(false);

  const fetchLaborCosts = useCallback(() => {
    authAxios.get('/api/laborcost')
      .then(res => setLaborCosts(res.data))
      .catch(err => console.error('Error fetching LaborCosts:', err));
  }, []);

  const fetchPackTypes = useCallback(() => {
    authAxios.get('/api/packtypes')
      .then(res => setPackTypes(res.data))
      .catch(err => console.error('Error fetching PackTypes:', err));
  }, []);

  useEffect(() => {
    fetchLaborCosts();
    fetchPackTypes();
  }, [fetchLaborCosts, fetchPackTypes]);

  // BagType can target a whole group (Type) or one specific style.
  const groups = useMemo(
    () => [...new Set(packTypes.map(p => p.Type).filter(Boolean))].sort(),
    [packTypes]
  );
  const styleNames = useMemo(
    () => [...new Set(packTypes.map(p => p.Style).filter(Boolean))].sort(),
    [packTypes]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const cleanedForm = {};
      for (let key in formData) {
        const value = formData[key];
        cleanedForm[key] = value === '' ? null : value;
      }
      if (isEditing) {
        await authAxios.put(`/api/laborcost/${editingId}`, cleanedForm);
      } else {
        await authAxios.post('/api/laborcost', cleanedForm);
      }
      setFormData(initialFormState);
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      fetchLaborCosts();
      savedToast('Labor Cost');
    } catch (err) {
      const msg = err.response?.data?.error;
      console.error('Error submitting LaborCost:', msg || err);
      errorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      BagType: row.BagType ?? '',
      People: row.People ?? '',
      BagsPerMinute: row.BagsPerMinute ?? '',
      WagesPerHour: row.WagesPerHour ?? '',
    });
    setShowForm(false);
    setIsEditing(true);
    setEditingId(row.UniqueID);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await authAxios.delete(`/api/laborcost/${id}`);
      fetchLaborCosts();
      deletedToast('Labor Cost');
    } catch (err) {
      console.error('Error deleting LaborCost:', err);
    }
  };

  const sortedData = useMemo(() => {
    const sortable = [...laborCosts];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        }
        return sortConfig.direction === 'asc'
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }
    return sortable;
  }, [laborCosts, sortConfig]);

  const filteredData = useMemo(() => {
    const lower = searchQuery.toLowerCase();
    return sortedData.filter(row =>
      Object.values(row).some(val =>
        String(val).toLowerCase().includes(lower)
      )
    );
  }, [searchQuery, sortedData]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  return (
    <StandardTableLayout
      title="Labor Costs"
      columns={visibleColumns}
      rows={filteredData}
      editingId={editingId}
      submitting={submitting}
      showForm={showForm}
      onToggleForm={() => {
        setShowForm(!showForm);
        setIsEditing(false);
        setFormData(initialFormState);
      }}
      onSubmit={handleSubmit}
      formData={formData}
      handleChange={handleChange}
      sortConfig={sortConfig}
      onSort={handleSort}
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      renderCell={(col, data, handleChange) => {
        if (!handleChange) {
          const text = (col === 'WagesPerHour' || col === 'LaborPerBag')
            ? fmt4(data?.[col])
            : String(data?.[col] ?? '');
          return (
            <span style={{ background: 'transparent', display: 'inline-block', padding: '4px 0' }}>
              {text}
            </span>
          );
        }
        if (col === 'BagType') {
          return (
            <Select name="BagType" aria-label="Group or Style" value={data.BagType || ''} onChange={handleChange}>
              <option value="">Select group or style</option>
              <optgroup label="Groups">
                {groups.map(g => <option key={`g-${g}`} value={g}>{g}</option>)}
              </optgroup>
              <optgroup label="Styles">
                {styleNames.map(s => <option key={`s-${s}`} value={s}>{s}</option>)}
              </optgroup>
            </Select>
          );
        }
        if (col === 'LaborPerBag') {
          return (
            <Input type="text" name={col} value={data[col] ?? ''} readOnly placeholder="(auto)" style={READONLY_STYLE} />
          );
        }
        const steps = { People: '1', BagsPerMinute: '1', WagesPerHour: '0.0001' };
        return (
          <Input
            type="number"
            step={steps[col]}
            name={col}
            value={data[col] ?? ''}
            placeholder={col === 'WagesPerHour' ? '$0.0000' : ''}
            onChange={handleChange}
          />
        );
      }}
      renderActions={(row) => (
        editingId === row.UniqueID ? (
          <>
            <Button type="submit" disabled={submitting}>Save</Button>
            <SecondaryButton
              type="button"
              onClick={() => {
                setIsEditing(false);
                setEditingId(null);
                setFormData(initialFormState);
              }}
            >
              Cancel
            </SecondaryButton>
          </>
        ) : (
          <>
            <SecondaryButton type="button" onClick={() => handleEdit(row)}>Edit</SecondaryButton>
            <DangerButton type="button" onClick={() => handleDelete(row.UniqueID)}>Delete</DangerButton>
          </>
        )
      )}
    />
  );
};

export default LaborCostsPage;
